import {
  useComments,
  useCreateComment,
  useEditComment,
  useDeleteComment
} from '@domain/shifthandover'
import {CardSectionTitle} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {usePermission} from '@hproduce/permissions'
import {LinearProgress, Box, Divider} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../consts'

import {NewComment} from './common/CommentForm'
import {CommentsList} from './common/CommentList'

type CommentsProps = {
  eventId: string
  plantId: string
  country: string
  hideAddComments?: boolean
}

export const Comments: React.FC<CommentsProps> = ({eventId, plantId, country, hideAddComments}) => {
  const timezone = useUpmPlantTimezone(plantId)
  const comments = useComments({plantId, eventId, timezone})
  const createComment = useCreateComment({plantId, timezone})
  const deleteCommentAction = useDeleteComment({plantId, timezone})
  const editCommentAction = useEditComment({plantId, timezone})

  const plantContext = {plantId, countryId: country}
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)

  const canAddComments = usePermission({
    plantContext,
    permissionTypes: ['ADD_PLANT_EVENT_COMMENTS']
  })
  const canViewComments = usePermission({
    plantContext,
    permissionTypes: ['VIEW_PLANT_EVENT_COMMENTS']
  })

  return (
    <Box data-test-id="events-comments">
      <Box
        sx={{
          mt: 3,
          mb: 1
        }}
      >
        <CardSectionTitle sx={{mb: 1}} data-test-id="events-comments-heading">
          {t('shiftEvent.label.comments')}
          {comments.data && ` (${comments.data.length})`}
        </CardSectionTitle>
        {comments.isFetching || comments.isPending ? (
          <LinearProgress data-test-id="events-comments-loading" />
        ) : (
          <Divider sx={{borderColor: 'divider'}} />
        )}
      </Box>
      {comments.data && (
        <Box my={2}>
          {!hideAddComments && canAddComments && (
            <NewComment eventId={eventId} createCommentAction={createComment} />
          )}
          {canViewComments && (
            <CommentsList
              plantId={plantId}
              country={country}
              comments={comments.data}
              withMarkdown={true}
              editCommentAction={editCommentAction}
              deleteCommentAction={deleteCommentAction}
            />
          )}
        </Box>
      )}
    </Box>
  )
}
